import { useInfiniteQuery } from '@tanstack/react-query';
import { get } from '../api/api';
import { API_URLS } from '../constants/apiUrl';
import { QueryParamObject, toQueryString } from '../constants/utilityFunctions';
interface IUseFetchTasks {
  startTime?: string;
  dueTime?: string;
  relations?: string[];
  sortBy?: string[];
  extraFilters?: Record<string, string | number | undefined>;
  pageSize?: number;
  search?: string;
  isCritical?: boolean;
  withHasMatterAccess?: boolean;
  fields?: string[];
  enabled: boolean;
}
export const useFetchTasks = (params: IUseFetchTasks) => {
  const fetchTasks = async ({ pageParam = 1 }) => {
    const {
      relations: tasksRelations,
      extraFilters: filters,
      pageSize,
      isCritical,
      enabled,
      ...rest
    } = params;
    const queryString = new URLSearchParams(
      Object.keys(rest)?.length ? toQueryString(rest as QueryParamObject) : '',
    );

    Object.entries(filters ?? {}).forEach(([key, value]) => {
      if (value) {
        queryString.append(`filter.${key}`, String(value));
      }
    });

    (tasksRelations ?? []).forEach((rel) => queryString.append('relations', rel));

    queryString.append('page', String(pageParam));
    queryString.append('limit', String(pageSize));
    try {
      if (!isCritical) {
        return await get(`${API_URLS.TASKS_LIST}?${queryString.toString()}`);
      } else {
        return await get(`${API_URLS.CRITICAL_TASKS_LIST}?${queryString.toString()}`);
      }
    } catch (error) {
      console.error('Error fetching Tasks:', error);
      throw new Error((error as { message: string }).message || 'Failed to fetch task'); // Force error throwing
    }
  };

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    refetch,
    isFetching,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['tasks-list', params],
    queryFn: fetchTasks,
    enabled: params.enabled,
    getNextPageParam: (lastPage, allPages) => {
      const totalCount = lastPage?.meta?.totalItems ?? 0;
      const fetchedCount = allPages.flatMap((page) => page.data).length;
      return fetchedCount < totalCount ? allPages.length + 1 : undefined;
    },
  });
  return {
    data: data?.pages.flatMap((page) => page.data) ?? [],
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    refetch,
    isFetching,
  };
};
